import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import axios from 'axios';
import { apiUrl } from '../../constant';

const POLL_INTERVAL_MS = 5000;
const TERMINAL_STATUSES = new Set(['Succeeded', 'Failed']);

const DailyDriveSyncModal = ({ show, onHide }) => {
    const [runId, setRunId] = useState(null);
    const [statusData, setStatusData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const pollTimerRef = useRef(null);

    const status = statusData?.status || null;
    const isActive = status === 'Queued' || status === 'Running';

    const clearPolling = useCallback(() => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    const fetchStatus = useCallback(async (id) => {
        try {
            const res = await axios.get(`${apiUrl}/api/DailyDriveSync/Status/${id}`);
            const entity = res?.data?.Entity;
            if (!entity) return;
            setStatusData(entity);
            if (TERMINAL_STATUSES.has(entity.status)) {
                clearPolling();
                if (entity.status === 'Succeeded') {
                    toast.success('Daily Drive Sync completed');
                } else {
                    toast.error(entity.errorSummary || 'Daily Drive Sync failed');
                }
            }
        } catch {
            // transient poll failure is ignored; the next tick retries
        }
    }, [clearPolling]);

    useEffect(() => {
        if (!runId || !isActive) return undefined;
        pollTimerRef.current = setInterval(() => fetchStatus(runId), POLL_INTERVAL_MS);
        return clearPolling;
    }, [runId, isActive, fetchStatus, clearPolling]);

    useEffect(() => () => clearPolling(), [clearPolling]);

    const handleRunNow = async () => {
        setIsSubmitting(true);
        try {
            const res = await axios.post(`${apiUrl}/api/DailyDriveSync/RunNow`, {});
            const entity = res?.data?.Entity;
            if (!entity) {
                toast.error('Unable to queue sync');
                return;
            }
            setRunId(entity.runId);
            setStatusData({ runId: entity.runId, status: 'Queued' });
            toast.success(entity.wasCreated ? 'Sync queued' : 'A sync is already in progress');
            fetchStatus(entity.runId);
        } catch (err) {
            const msg = err?.response?.status === 403
                ? 'Access denied — SuperAdmin only'
                : (err?.response?.data?.message || 'Failed to queue sync');
            toast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        clearPolling();
        onHide();
    };

    const formatDate = (v) => (v ? new Date(v).toLocaleString() : '');

    let runButtonLabel = 'Run Sync Now';
    if (isActive) {
        runButtonLabel = 'Running…';
    } else if (isSubmitting) {
        runButtonLabel = 'Queuing…';
    }

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Daily Drive Sync</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!statusData && (
                    <p className="mb-0">
                        Backs up packing labels, pick-lists, and task attachments to Google Drive.
                        Click <strong>Run Sync Now</strong> to trigger a run.
                    </p>
                )}
                {statusData && (
                    <div>
                        <div><strong>Run ID:</strong> {statusData.runId}</div>
                        <div><strong>Status:</strong> {statusData.status}{isActive ? ' (polling every 5s)' : ''}</div>
                        {statusData.startedOn && (
                            <div><strong>Started:</strong> {formatDate(statusData.startedOn)}</div>
                        )}
                        {statusData.completedOn && (
                            <div><strong>Completed:</strong> {formatDate(statusData.completedOn)}</div>
                        )}
                        <div>
                            <strong>Tasks:</strong>{' '}
                            {statusData.tasksSucceeded ?? 0}/{statusData.tasksProcessed ?? 0}
                            {statusData.tasksFailed > 0 && ` (${statusData.tasksFailed} failed)`}
                        </div>
                        <div>
                            <strong>Labels:</strong>{' '}
                            {statusData.labelsSucceeded ?? 0}/{statusData.labelsProcessed ?? 0}
                            {statusData.labelsFailed > 0 && ` (${statusData.labelsFailed} failed)`}
                        </div>
                        {statusData.errorSummary && (
                            <div className="text-danger mt-2">
                                <strong>Error:</strong> {statusData.errorSummary}
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="primary"
                    onClick={handleRunNow}
                    disabled={isSubmitting || isActive}
                >
                    {runButtonLabel}
                </Button>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

DailyDriveSyncModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
};

export default DailyDriveSyncModal;
